/**
 * Buggino v2 – interaction layer for the photo-first design.
 * Reveal-on-scroll, product carousels, mega menu (touch + keyboard), header shadow,
 * delivery date line and sticky mobile add-to-cart on product pages.
 */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal on scroll ---------- */
  function initReveal() {
    const nodes = document.querySelectorAll('.reveal');
    if (!nodes.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      nodes.forEach((n) => n.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px 160px 0px', threshold: 0 }
    );
    nodes.forEach((n) => io.observe(n));
  }

  /* ---------- Carousels ---------- */
  function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach((root) => {
      const track = root.querySelector('[data-carousel-track]');
      const prev = root.querySelector('[data-carousel-prev]');
      const next = root.querySelector('[data-carousel-next]');
      if (!track || !prev || !next) return;

      const step = () => {
        const first = track.firstElementChild;
        if (!first) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0') || 0;
        return first.getBoundingClientRect().width + gap;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
      };
      prev.addEventListener('click', () => track.scrollBy({ left: -step() * 2, behavior: reduceMotion ? 'auto' : 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: step() * 2, behavior: reduceMotion ? 'auto' : 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- Mega menu: touch + keyboard ---------- */
  function initMegaMenu() {
    const items = document.querySelectorAll('.nav__item--mega');
    if (!items.length) return;
    const hoverCapable = window.matchMedia('(hover: hover)').matches;

    const closeAll = (except) => {
      items.forEach((item) => {
        if (item === except) return;
        item.classList.remove('is-open');
        const trigger = item.querySelector('[data-mega-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    };

    items.forEach((item) => {
      const trigger = item.querySelector('[data-mega-trigger]');
      if (!trigger) return;
      trigger.addEventListener('click', (event) => {
        // First tap on touch devices opens the panel instead of navigating.
        if (!hoverCapable && !item.classList.contains('is-open')) {
          event.preventDefault();
          closeAll(item);
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === ' ') {
          event.preventDefault();
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          const firstLink = item.querySelector('.mega a');
          if (firstLink) firstLink.focus();
        }
      });
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          item.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
          trigger.focus();
        }
      });
      item.addEventListener('mouseenter', () => trigger.setAttribute('aria-expanded', 'true'));
      item.addEventListener('mouseleave', () => {
        trigger.setAttribute('aria-expanded', 'false');
        item.classList.remove('is-open');
      });
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.nav__item--mega')) closeAll(null);
    });
  }

  /* ---------- Header shadow after scrolling ---------- */
  function initHeaderShadow() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ---------- Delivery date line (3–7 business days, skipping weekends) ---------- */
  function addBusinessDays(date, days) {
    const d = new Date(date.getTime());
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return d;
  }

  function initDeliveryLine() {
    const line = document.querySelector('[data-delivery-line]');
    if (!line) return;
    const text = line.querySelector('[data-delivery-text]');
    if (!text) return;
    const min = Number(line.dataset.minDays || 3);
    const max = Number(line.dataset.maxDays || 7);
    const locale = line.dataset.locale || document.documentElement.lang || 'de';
    const now = new Date();
    // Orders after 14:00 ship the next business day.
    const start = now.getHours() >= 14 ? addBusinessDays(now, 1) : now;
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' });
    const from = fmt.format(addBusinessDays(start, min));
    const to = fmt.format(addBusinessDays(start, max));
    text.textContent = (text.dataset.template || '[from] – [to]').replace('[from]', from).replace('[to]', to);
    line.removeAttribute('hidden');
  }

  /* ---------- Sticky mobile add-to-cart ---------- */
  function initStickyAtc() {
    const sticky = document.querySelector('[data-sticky-atc]');
    const main = document.querySelector('[data-add-to-cart]');
    if (!sticky || !main) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      const rect = main.getBoundingClientRect();
      const passed = rect.bottom < 0;
      sticky.classList.toggle('is-visible', passed);
      sticky.setAttribute('aria-hidden', passed ? 'false' : 'true');
      // Chat- und Punkte-Button über die Leiste heben, solange sie sichtbar ist (nur mobil)
      document.documentElement.classList.toggle('sticky-atc-on', passed && window.matchMedia('(max-width: 899px)').matches);
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();

    const price = sticky.querySelector('[data-sticky-price]');
    const button = sticky.querySelector('[data-sticky-submit]');
    document.addEventListener('variant:change', (event) => {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      if (button) {
        button.disabled = !variant.available;
        if (!button.querySelector('[data-sticky-label]')) {
          button.textContent = variant.available ? (main.textContent || '').trim() : button.textContent;
        }
      }
      if (price && main.closest('[data-product-root]')) {
        const format = main.closest('[data-product-root]').dataset.moneyFormat || '{{amount}}';
        price.textContent = formatMoney(variant.price, format);
      }
    });
  }

  function formatMoney(cents, format) {
    const value = (Number(cents) / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const comma = `${grouped},${dec}`;
    const plain = `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${dec}`;
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, comma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, grouped)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, int)
      .replace(/\{\{\s*amount\s*\}\}/, plain);
  }

  function initVariantSku() {
    const targets = document.querySelectorAll('[data-variant-sku]');
    if (!targets.length) return;
    document.addEventListener('variant:change', (event) => {
      const variant = event.detail && event.detail.variant;
      if (!variant || !variant.sku) return;
      targets.forEach((el) => {
        el.textContent = variant.sku;
      });
    });
  }

  function init() {
    initReveal();
    initCarousels();
    initMegaMenu();
    initHeaderShadow();
    initDeliveryLine();
    initStickyAtc();
    initVariantSku();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
